import { Link } from '@inertiajs/react'
import { Badge, Box, Button, Flex, Grid, Select, Text, TextField } from '@radix-ui/themes'
import { Car, Instagram, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import CardBrowser, { BrowseGroup, CardThumb, FacetChip, showFilters, toggled } from '@/shared/browse/CardBrowser'
import ColorSwatch from './ColorSwatch'
import ReportCarDialog, { ReportCarLink } from './ReportCarDialog'
import { CarPhotoGrid, CarSpecList } from './CarDetails'
import { useBrand } from '@/shared/brand/useBrand'
import {
  CarFilters,
  NO_FILTERS,
  carMatches,
  colorFacets,
  filtersActive,
  makeFacets,
  modelFacets,
} from './carFilters'
import { EventsShowReportReason, EventsShowBrowsableCar as Driver } from '@/types/events_show'

interface Props<T extends Driver> {
  groups: BrowseGroup<T>[]
  /** CarReport::REASONS, for the "What's wrong?" dialog. */
  reportReasons: EventsShowReportReason[]
}

const ANY = 'any'

/**
 * The cars at an event, public to everyone: filter by what people remember
 * (color, make, model, or anything typed), then open a card in place for the
 * photos and details.
 */
export default function CarBrowser<T extends Driver>({ groups, reportReasons }: Props<T>) {
  const [filters, setFilters] = useState<CarFilters>(NO_FILTERS)
  // Held here, not in the card: a counted report drops the car from the list.
  const [reporting, setReporting] = useState<Driver | null>(null)
  const allCars = useMemo(() => groups.flatMap((g) => g.items), [groups])

  const colors = useMemo(() => colorFacets(allCars), [allCars])
  const makes = useMemo(() => makeFacets(allCars), [allCars])
  const models = useMemo(() => modelFacets(allCars, filters.make), [allCars, filters.make])

  const filtered = groups.map((group) => ({ ...group, items: group.items.filter((car) => carMatches(car, filters)) }))

  return (
    <>
      <CardBrowser
        groups={filtered}
        total={allCars.length}
        active={filtersActive(filters)}
        onClear={() => setFilters(NO_FILTERS)}
        noMatch="No cars match."
        // A car is listed once per event (one report per participant), and
        // once on a track page.
        getKey={(car) => car.id}
        renderSummary={(car) => <CarSummary car={car} />}
        renderDetail={(car) => <CarDetail car={car} onReport={setReporting} />}
        filters={
          showFilters(allCars.length) && (
            <>
              <Flex gap="2" wrap="wrap">
                {colors.map(({ color, count }) => (
                  <FacetChip
                    key={color.value}
                    on={filters.colors.includes(color.value)}
                    onClick={() => setFilters({ ...filters, colors: toggled(filters.colors, color.value) })}
                  >
                    <ColorSwatch color={color} size={12} />
                    {color.label} {count}
                  </FacetChip>
                ))}
              </Flex>

              <Grid columns={{ initial: '1', sm: '3' }} gap="2">
                <Select.Root
                  value={filters.make ?? ANY}
                  onValueChange={(v) => setFilters({ ...filters, make: v === ANY ? null : v, model: null })}
                >
                  <Select.Trigger aria-label="Make" />
                  <Select.Content>
                    <Select.Item value={ANY}>Any make</Select.Item>
                    {makes.map((make) => (
                      <Select.Item key={make.key} value={make.key}>
                        {make.label} ({make.count})
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                <Select.Root
                  value={filters.model ?? ANY}
                  disabled={filters.make === null}
                  onValueChange={(v) => setFilters({ ...filters, model: v === ANY ? null : v })}
                >
                  <Select.Trigger aria-label="Model" placeholder="Any model" />
                  <Select.Content>
                    <Select.Item value={ANY}>Any model</Select.Item>
                    {models.map((model) => (
                      <Select.Item key={model.key} value={model.key}>
                        {model.label} ({model.count})
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>

                <TextField.Root
                  placeholder="Driver, handle, wheels, kit…"
                  value={filters.query}
                  onChange={(e) => setFilters({ ...filters, query: e.target.value })}
                >
                  <TextField.Slot>
                    <Search size={14} />
                  </TextField.Slot>
                </TextField.Root>
              </Grid>
            </>
          )
        }
      />

      <ReportCarDialog car={reporting} reasons={reportReasons} onClose={() => setReporting(null)} />
    </>
  )
}

function CarSummary({ car }: { car: Driver }) {
  const heading = [car.year, car.make, car.model].filter(Boolean).join(' ')
  return (
    <Flex gap="3" align="center">
      <CardThumb src={car.cover_thumb_url} fallback={<Car size={28} color="var(--gray-8)" />} />
      <Box style={{ minWidth: 0 }}>
        <Flex align="center" gap="2">
          {car.color_family && <ColorSwatch color={car.color_family} size={14} />}
          <Text weight="bold" truncate>
            {car.color_family && `${car.color_family.label} `}
            {heading}
          </Text>
        </Flex>
        <Text as="div" size="2" color="gray" truncate>
          {car.driver_name}
          {car.instagram && ` · @${car.instagram}`}
        </Text>
        {car.review_status === 'pending' && <Badge color="amber" mt="1">Pending review — only you can see this</Badge>}
        {car.review_status === 'rejected' && <Badge color="red" mt="1">Not approved — only you can see this</Badge>}
      </Box>
    </Flex>
  )
}

function CarDetail({ car, onReport }: { car: Driver; onReport: (car: Driver) => void }) {
  // A car's page isn't part of a branded site.
  const brand = useBrand()

  return (
    <Flex direction="column" gap="3">
      <CarPhotoGrid photos={car.photos} driverName={car.driver_name} />
      <CarSpecList specs={car.specs} />

      <Flex justify="between" align="center" gap="3" wrap="wrap">
        {car.instagram ? (
          <Button asChild variant="soft" color="gray">
            <a href={`https://instagram.com/${car.instagram}`} target="_blank" rel="noopener noreferrer">
              <Instagram size={16} />@{car.instagram}
            </a>
          </Button>
        ) : (
          <span />
        )}
        <Flex align="center" gap="3">
          {!brand && (
            <Link href={`/driver_cars/${car.id}`} className="text-accent9 text-sm">
              Where's this car been?
            </Link>
          )}
          <ReportCarLink car={car} onReport={onReport} />
        </Flex>
      </Flex>
    </Flex>
  )
}
